import { API_BASE_URL } from '@/services/api';

export interface SyncForecastResult {
  forecast_run_id: number;
  forecast_lines_created: number;
  consensus_lines_created: number;
  consensus_lines_touched: number;
  plan_version_id?: number;
  consensus_lines_purged?: number;
}

export interface SyncJobStatus {
  status: 'idle' | 'running' | 'completed' | 'failed';
  planning_cycle_id: number;
  started_at?: string;
  finished_at?: string;
  result?: SyncForecastResult;
  error?: string;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const data = await response.json();
  if (!response.ok) {
    const message =
      (typeof data.detail === 'string' && data.detail) ||
      (typeof data.message === 'string' && data.message) ||
      'Sync error';
    const err = new Error(message) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }
  return data as T;
}

async function authHeaders(): Promise<Headers> {
  const headers = new Headers({ 'Content-Type': 'application/json' });
  const token = localStorage.getItem('auth_token');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return headers;
}

export async function fetchSyncJobStatus(cycleId: number): Promise<SyncJobStatus> {
  const headers = await authHeaders();
  const response = await fetch(
    `${API_BASE_URL}/demand-planning/planning-cycles/${cycleId}/sync-forecast-status/`,
    { method: 'GET', headers },
  );
  return parseJsonResponse<SyncJobStatus>(response);
}

export async function pollSyncJobUntilDone(
  cycleId: number,
  options?: { intervalMs?: number; maxAttempts?: number },
): Promise<SyncForecastResult> {
  const intervalMs = options?.intervalMs ?? 2000;
  const maxAttempts = options?.maxAttempts ?? 120;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const job = await fetchSyncJobStatus(cycleId);
    if (job.status === 'completed' && job.result) {
      return job.result;
    }
    if (job.status === 'failed') {
      throw new Error(job.error || 'Sync failed');
    }
    if (job.status === 'idle' && attempt > 2) {
      throw new Error('Sync job not found or already finished');
    }
    await sleep(intervalMs);
  }
  throw new Error('Sync timed out');
}

export async function startSyncForecastBootstrap(
  cycleId: number,
  options?: { force?: boolean; wait?: boolean },
): Promise<SyncForecastResult> {
  const headers = await authHeaders();
  const response = await fetch(
    `${API_BASE_URL}/demand-planning/planning-cycles/${cycleId}/sync-forecast-bootstrap/`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({
        force: Boolean(options?.force),
        async: options?.wait ? false : true,
        wait: Boolean(options?.wait),
      }),
    },
  );

  if (response.status === 202) {
    await response.json();
    return pollSyncJobUntilDone(cycleId);
  }

  const payload = await parseJsonResponse<SyncForecastResult>(response);
  return payload;
}
